import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, BlogPost } from '../../../lib/supabase';

export default function Features() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('published', true)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setPosts(data || []);
      } catch {
        // 接続エラー時は空白表示
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <section id="features" className="py-24 bg-gradient-to-b from-white to-amber-50/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">新着記事</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              高級腕時計の世界を、より深く、より正確に理解していただくための充実した機能とサービス
            </p>
          </div>
          <div className="flex justify-center items-center py-20">
            <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return (
      <section id="features" className="py-24 bg-gradient-to-b from-white to-amber-50/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">新着記事</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              高級腕時計の世界を、より深く、より正確に理解していただくための充実した機能とサービス
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="features" className="py-24 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">新着記事</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            高級腕時計の世界を、より深く、より正確に理解していただくための充実した機能とサービス
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.id}`}
              className="group bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-amber-200 transition-all duration-300 cursor-pointer"
            >
              <div className="relative h-56 w-full overflow-hidden">
                <img
                  src={
                    post.image_url ||
                    'https://readdy.ai/api/search-image?query=luxury%20watch%20elegant%20timepiece%20on%20sophisticated%20dark%20background%20with%20soft%20lighting%20highlighting%20craftsmanship%20and%20premium%20details&width=800&height=600&seq=home-blog-default&orientation=landscape'
                  }
                  alt={post.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center text-sm text-gray-400 mb-3 gap-2">
                  <i className="ri-calendar-line"></i>
                  <span>{formatDate(post.created_at)}</span>
                  <span>•</span>
                  <span>{post.author}</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-3 group-hover:text-amber-600 transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-gray-500 text-sm line-clamp-3 mb-4 leading-relaxed">
                  {post.content.replace(/<[^>]*>/g, '').substring(0, 120)}...
                </p>
                <div className="flex items-center gap-1 text-amber-600 text-sm font-medium">
                  <span className="whitespace-nowrap">続きを読む</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
